import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, Plus, Printer, X } from 'lucide-react';
import { openLibraryForPrint } from './LibraryPreview.js';
import type { LibraryDocument } from '@worki/shared';
import { api, authedFetch, RequestError } from '../../lib/api.js';
import { standardFontDataUrl } from '../../lib/pdfFonts.js';
import { Button } from '../../components/ui.js';
import { Modal } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';

/**
 * 1.44 — the pencil on a stored document: a full edit, not only its name.
 *
 * The document's pages are a grid, drawn the way the scan's review draws its
 * own: numbered, each with an X. "הוספת קבצים" puts the pages of more PDFs or
 * photographs at the end, and a file that was added is a tile of its own that
 * has the same X. The title is still here. Pages are taken out and added, never
 * moved — the order the document has is the order it keeps.
 *
 * Nothing is sent until "שמירה", and what is sent is small: the numbers of the
 * pages that stay, and the files that were added. The server builds the new PDF
 * and puts it in place of the old one under the same id.
 */

/** What the picker offers: the files pdf-lib can take in as pages. */
const ACCEPT = 'application/pdf,.pdf,image/jpeg,image/png';

/** Wide enough to be sharp in a tile on a phone, small enough that forty pages are cheap to hold. */
const THUMB_WIDTH = 240;

let fileSeq = 0;

/** A PDF, and the photographs pdf-lib can draw, have pages to cut. Word and the rarer picture types do not. */
export function canEditPages(mime: string): boolean {
  return mime === 'application/pdf' || mime === 'image/jpeg' || mime === 'image/png';
}

/**
 * The save, as the multipart form the route reads: the page numbers are of the
 * file as it was, as JSON text beside the files, and `updatedAt` is the version
 * this screen opened, so a file that changed meanwhile is refused and not cut
 * at the wrong pages.
 */
export function editForm(edit: { title: string; updatedAt: string; keep: number[]; files: File[] }): FormData {
  const form = new FormData();
  form.append('title', edit.title);
  form.append('updatedAt', edit.updatedAt);
  form.append('keep', JSON.stringify(edit.keep));
  for (const file of edit.files) form.append('files', file, file.name);
  return form;
}

/**
 * What may be saved, and whether to say why it cannot be.
 *
 * A file whose pages could not be drawn is still renamed, which is all the
 * dialog this replaced did; everything else needs its pages known and one left.
 */
export function saveRules(state: { title: string; loaded: boolean; failed: boolean; remaining: number }): {
  canSave: boolean;
  needsPage: boolean;
} {
  const titled = state.title.trim() !== '';
  if (state.failed) return { canSave: titled, needsPage: false };
  return { canSave: titled && state.loaded && state.remaining > 0, needsPage: state.loaded && state.remaining === 0 };
}

export interface EditTile {
  key: string;
  /** A picture of the page, or of a photograph that was added. Null while a page is still being drawn. */
  thumb: string | null;
  /** Set for a file that was added: a PDF is its name, as it is in the scan's review. */
  name?: string;
}

/*
 * The scan review's own grid and tile (`QuickScan`), without its arrows: a page
 * here is taken out or left, not moved. The number is where the page stands now,
 * so taking page 2 out makes 3 read 2.
 */
export function EditGrid({ tiles, onRemove }: { tiles: EditTile[]; onRemove: (key: string) => void }) {
  const { t } = useTranslation();
  return (
    <ul className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {tiles.map((tile, index) => (
        <li key={tile.key} className="relative rounded-lg border border-border bg-muted overflow-hidden aspect-[3/4]">
          {tile.thumb ? (
            <img src={tile.thumb} alt="" className="w-full h-full object-contain" />
          ) : tile.name ? (
            <span className="w-full h-full flex flex-col items-center justify-center gap-1 text-muted-foreground">
              <FileText className="h-6 w-6" aria-hidden />
              <span className="text-[10px] px-1 truncate max-w-full">{tile.name}</span>
            </span>
          ) : (
            <span className="w-full h-full flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            </span>
          )}

          <span className="absolute top-1 start-1 px-1.5 rounded-md bg-background/85 text-[11px] font-semibold tabular-nums">
            {index + 1}
          </span>

          <button
            type="button"
            onClick={() => onRemove(tile.key)}
            aria-label={t('scan.removePage', { index: index + 1 })}
            className="absolute top-1 end-1 p-1 rounded-md bg-background/85 text-destructive hover:bg-destructive/10"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </li>
      ))}
    </ul>
  );
}

interface EditPage {
  /** 1-based, in the stored file — what the server is told to keep. */
  number: number;
  thumb: string | null;
}

interface AddedFile {
  id: string;
  file: File;
  /** A photograph shows itself; a PDF is its name. */
  url: string | null;
}

/**
 * The pages of a PDF, drawn one at a time: the grid has its tiles, and their X,
 * before the first picture is ready, and closing the dialog stops the drawing.
 * pdf.js is loaded here, as `PdfPreview` does, so it lands only on whoever opens it.
 */
async function drawPdfPages(
  bytes: Uint8Array,
  alive: () => boolean,
  onCount: (count: number) => void,
  onThumb: (number: number, thumb: string) => void,
): Promise<void> {
  const [{ getDocument, GlobalWorkerOptions }, { default: PdfWorker }] = await Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs'),
    import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker'),
  ]);
  const worker = new PdfWorker();
  GlobalWorkerOptions.workerPort = worker;

  const task = getDocument({ data: bytes, standardFontDataUrl });
  try {
    const pdf = await task.promise;
    // The dialog may have been closed, or opened on another document, while the file loaded.
    if (!alive()) return;
    onCount(pdf.numPages);
    for (let number = 1; number <= pdf.numPages; number += 1) {
      if (!alive()) return;
      const page = await pdf.getPage(number);
      const unscaled = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: THUMB_WIDTH / unscaled.width });

      const canvas = window.document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('no 2d context');
      await page.render({ canvas, canvasContext: context, viewport }).promise;
      if (alive()) onThumb(number, canvas.toDataURL('image/jpeg', 0.7));
      page.cleanup();
    }
  } finally {
    void task.destroy();
    worker.terminate();
  }
}

export function LibraryEditor({ document, onClose }: { document: LibraryDocument | null; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const pickRef = useRef<HTMLInputElement>(null);
  /** Every picture this dialog made a URL for, so that closing it lets go of them all. */
  const urls = useRef(new Set<string>());

  const [title, setTitle] = useState('');
  /** The stored file's pages once they are counted; null until then. Each is drawn as it is ready. */
  const [pages, setPages] = useState<EditPage[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [removed, setRemoved] = useState<Set<number>>(new Set());
  const [added, setAdded] = useState<AddedFile[]>([]);

  const urlFor = (blob: Blob): string => {
    const url = URL.createObjectURL(blob);
    urls.current.add(url);
    return url;
  };

  useEffect(() => {
    setTitle(document?.title ?? '');
    setPages(null);
    setFailed(false);
    setRemoved(new Set());
    setAdded([]);
    if (!document) return undefined;

    let alive = true;
    const draw = async () => {
      const response = await authedFetch(`/library/${document.id}/file`);
      if (!response.ok) throw new Error(String(response.status));
      if (document.mime !== 'application/pdf') {
        // A photograph is its own single page.
        const picture = urlFor(await response.blob());
        if (alive) setPages([{ number: 1, thumb: picture }]);
        return;
      }
      await drawPdfPages(
        new Uint8Array(await response.arrayBuffer()),
        () => alive,
        (count) => setPages(Array.from({ length: count }, (_, index) => ({ number: index + 1, thumb: null }))),
        (number, thumb) =>
          setPages((current) => current && current.map((page) => (page.number === number ? { ...page, thumb } : page))),
      );
    };
    draw().catch(() => {
      if (alive) setFailed(true);
    });

    return () => {
      alive = false;
      urls.current.forEach((url) => URL.revokeObjectURL(url));
      urls.current.clear();
    };
  }, [document?.id]);

  const kept = (pages ?? []).filter((page) => !removed.has(page.number));
  const remaining = kept.length + added.length;
  const rules = saveRules({ title, loaded: pages !== null, failed, remaining });
  // Nothing on the grid was touched: the title alone goes, and the file is not rebuilt for nothing.
  const changed = !failed && (removed.size > 0 || added.length > 0);

  const tiles: EditTile[] = [
    ...kept.map((page) => ({ key: `page-${page.number}`, thumb: page.thumb })),
    ...added.map((item) => ({ key: item.id, thumb: item.url, name: item.file.name })),
  ];

  const removeTile = (key: string) => {
    if (key.startsWith('page-')) {
      setRemoved((current) => new Set(current).add(Number(key.slice('page-'.length))));
      return;
    }
    const item = added.find((candidate) => candidate.id === key);
    if (item?.url) {
      URL.revokeObjectURL(item.url);
      urls.current.delete(item.url);
    }
    setAdded((current) => current.filter((candidate) => candidate.id !== key));
  };

  const save = useMutation({
    mutationFn: ({ row, print }: { row: LibraryDocument; print?: boolean }) =>
      print && !changed && title.trim() === row.title ? Promise.resolve(row) : changed
        ? api.upload<LibraryDocument>(
            `/library/${row.id}/pages`,
            editForm({ title: title.trim(), updatedAt: row.updatedAt, keep: kept.map((page) => page.number), files: added.map((item) => item.file) }),
          )
        : api.patch<LibraryDocument>(`/library/${row.id}`, { title: title.trim() }),
    onSuccess: async (saved, action) => {
      void queryClient.invalidateQueries({ queryKey: ['library'] });
      if (action.print) {
        try { await openLibraryForPrint(saved); }
        catch (cause) { toast.error(t(cause instanceof RequestError ? cause.code : 'errors.unknown')); }
      }
      else toast.success(t('common.saved'));
      onClose();
    },
    onError: (cause) => {
      const failure = cause instanceof RequestError ? cause : null;
      toast.error(t(failure?.fields?.keep ?? failure?.fields?.files ?? failure?.code ?? 'errors.unknown'));
    },
  });

  return (
    <Modal
      open={Boolean(document)}
      title={t('library.editTitle')}
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button" variant="secondary" icon={<Printer className="h-4 w-4" />} disabled={!rules.canSave || save.isPending}
            onClick={() => document && save.mutate({ row: document, print: true })}
          >{t(changed || title.trim() !== document?.title ? 'library.saveAndPrint' : 'common.print')}</Button>
          <Button
            type="button"
            variant="primary"
            loading={save.isPending}
            disabled={!rules.canSave}
            onClick={() => document && save.mutate({ row: document })}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block text-sm space-y-1">
          <span className="text-muted-foreground">{t('library.titleLabel')}</span>
          <input
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={160}
            className="w-full h-10 px-3 rounded-lg bg-muted text-sm outline-none"
          />
        </label>

        {failed ? (
          <p className="text-sm text-muted-foreground">{t('library.editPagesFailed')}</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">{t('library.editHint')}</p>
            {pages === null ? (
              <p className="py-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>
            ) : (
              <EditGrid tiles={tiles} onRemove={removeTile} />
            )}
            {rules.needsPage && (
              <p role="alert" className="text-xs text-destructive">
                {t('validation.library.noPages')}
              </p>
            )}
            <Button
              type="button"
              variant="secondary"
              icon={<Plus className="h-4 w-4" aria-hidden />}
              onClick={() => pickRef.current?.click()}
            >
              {t('library.addFiles')}
            </Button>
            <input
              ref={pickRef}
              type="file"
              accept={ACCEPT}
              multiple
              className="sr-only"
              onChange={(event) => {
                const files = [...(event.target.files ?? [])];
                event.target.value = '';
                setAdded((current) => [
                  ...current,
                  ...files.map((file) => ({
                    id: `file-${(fileSeq += 1)}`,
                    file,
                    url: file.type.startsWith('image/') ? urlFor(file) : null,
                  })),
                ]);
              }}
            />
          </>
        )}
      </div>
    </Modal>
  );
}
